/**
 * A person's name, as a link to their profile — V5-08 §1.
 *
 * One component so that "click a person, see their work" is true everywhere a
 * person is rendered, rather than true on the two screens someone remembered
 * to wire up.
 *
 * Guests never get the link: the profile route refuses them, and a link that
 * leads to a refusal is worse than no link.
 */

import { Link } from 'react-router';
import { MessageSquare } from 'lucide-react';
import { UserAvatar } from '@/components/ui/avatar';
import { useAuth } from '@/features/auth';
import { cn } from '@/lib/utils';

export interface UserLinkProps {
  userId: string;
  name: string;
  avatarUrl?: string;
  /** Avatar only, for dense rows and assignee stacks. */
  avatarOnly?: boolean;
  /** Show the "Message" action beside the name (§5). */
  showMessage?: boolean;
  /**
   * Name only — for places that already render an avatar of their own.
   *
   * Distinct from `size={0}`, which still renders an avatar: a zero-sized one
   * shows its fallback initial, so the row reads "PParas".
   */
  nameOnly?: boolean;
  size?: number;
  className?: string;
}

export function UserLink({
  userId,
  name,
  avatarUrl,
  avatarOnly = false,
  nameOnly = false,
  showMessage = false,
  size = 20,
  className,
}: UserLinkProps) {
  const { user } = useAuth();
  const isGuest = user?.systemRole === 'guest';

  const content = avatarOnly ? (
    <UserAvatar id={userId} name={name} src={avatarUrl} size={size} />
  ) : nameOnly ? (
    <>{name}</>
  ) : (
    <span className="inline-flex items-center gap-1.5">
      <UserAvatar id={userId} name={name} src={avatarUrl} size={size} />
      <span className="truncate">{name}</span>
    </span>
  );

  if (isGuest) {
    return <span className={cn('inline-flex items-center', className)}>{content}</span>;
  }

  // §5 — a way into a conversation from wherever a person is named. Rendered
  // beside the profile link rather than inside it: a link nested in a link is
  // neither valid nor clickable, and the two go to different places.
  const messageAction = showMessage && userId !== user?._id && (
    <Link
      to={`/chat?with=${userId}`}
      data-testid="user-message-action"
      title={`Message ${name}`}
      className="ml-1 inline-flex opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--accent)]"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <MessageSquare className="size-3" />
    </Link>
  );

  if (messageAction) {
    return (
      <span className={cn('group inline-flex items-center', className)}>
        <Link
          to={`/people/${userId}`}
          data-testid="user-link"
          data-user-id={userId}
          title={`Open ${name}'s profile`}
          className="inline-flex items-center rounded-[4px] hover:text-[var(--accent)] hover:underline"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {content}
        </Link>
        {messageAction}
      </span>
    );
  }

  return (
    <Link
      to={`/people/${userId}`}
      data-testid="user-link"
      data-user-id={userId}
      title={`Open ${name}'s profile`}
      className={cn(
        'inline-flex items-center rounded-[4px] hover:text-[var(--accent)] hover:underline',
        className,
      )}
      // Rows and cards are frequently clickable themselves; without this the
      // parent's handler fires as well and the profile opens *and* the task.
      onClick={(e) => e.stopPropagation()}
      // Board cards are draggable. Without this, pressing on an avatar starts
      // a drag and the link never fires.
      onPointerDown={(e) => e.stopPropagation()}
    >
      {content}
    </Link>
  );
}
